import { useEffect, useState } from "react";
import { Space } from "antd";
import QRCode from "qrcode.react";
import {
  QRCODE_QUERY_MAP,
  rechargeChannelsCode,
} from "project-config/constants";
import RefreshIcon from "@/assets/images/refresh.png";
import { useTranslation } from "react-i18next";
import { isDev } from "project-config/utils/env";
import Wechat from "@/assets/images/pay-wechat.png";
import Alipay from "@/assets/images/alipay.png";
import { useGlobalStore } from "@/store/GlobalStore";
import { getUserKey } from "@/utils/getUesrKey";
import { RightOutlined } from "@ant-design/icons";
import { useTimestamp } from "@/hooks/use-timestamp";
import { useAnimationTopUpStore } from "../../store";
import {
  Gift,
  HiddenTopLeft,
  PayType,
  PaymentContent,
  PaymentWayWrapper,
  Price,
  QRCodeContainer,
  QRCodeLoading,
  RefreshButton,
} from "./atom";

import { PaySuccessSocket } from "../pay-socket";
import { BalanceTipType, ExtraWelfareTipType } from "../../types";
import { useUserPropertyStore } from "@/store2/userProperty/user-property-store";

export function PaymentWay({
  qrcodeLink,
  onClickCantScanCode,
  paying,
  balanceTip,
  extraTip,
  qingyunTip,
  desktopTip,
  style,
}: {
  qrcodeLink?: string;
  onClickCantScanCode?: () => void;
  paying?: boolean;
  balanceTip?: BalanceTipType[];
  extraTip?: ExtraWelfareTipType[];
  qingyunTip?: ExtraWelfareTipType[];
  desktopTip?: ExtraWelfareTipType[];
  style?: object;
}) {
  const { userInfo, languageFlag, currencySymbol, isCertified } =
    useGlobalStore();
  const { userProperty } = useUserPropertyStore();
  const isSubmitTask = userProperty?.isSubmitTask;
  const [localTimestamp, setLocalTimestamp] = useState(Date.now());
  const { timestamp, fetchTimestamp } = useTimestamp();
  const hasScanned = useAnimationTopUpStore((state) => state.hasScanned);
  const amount = useAnimationTopUpStore((state) => state.amount);
  const { setter: setAnimationTopUpStore } = useAnimationTopUpStore();
  const { t } = useTranslation("topup");
  const userKey = getUserKey();
  const host = isDev()
    ? "https://task-pre.renderbus.com"
    : "https://task.renderbus.com";
  const decimal = String(amount).split(".")[1] || "";
  const wholeText = () => {
    const noCertifiedSubmit =
      !isCertified && !Boolean(isSubmitTask) ? "1" : "0";
    let paramStr = `${host}/mobile-pay?${QRCODE_QUERY_MAP.platform}=${userInfo?.platform}&${QRCODE_QUERY_MAP.timeStamp}=${timestamp}&${QRCODE_QUERY_MAP.languageFlag}=${languageFlag}&${QRCODE_QUERY_MAP.userKey}=${userKey}${qrcodeLink}&${QRCODE_QUERY_MAP.noCertifiedSubmit}=${noCertifiedSubmit}&${QRCODE_QUERY_MAP.rechargeChannels}=${rechargeChannelsCode.Web}`;
    return paramStr;
  };

  const disabledQrCode = () => {
    return (
      !amount ||
      Number.isNaN(amount) ||
      0.01 > amount ||
      amount > 200000 ||
      decimal.length > 2
    );
  };

  const balanceTipList = balanceTip?.filter(
    (item: any) => item.content.length > 0
  );
  const extraTipList = extraTip?.filter((item: any) => item.content.length > 0);
  const qingyunTipList = qingyunTip?.filter(
    (item: any) => item.content.length > 0
  );
  const desktopTipList = desktopTip?.filter(
    (item: any) => item.content.length > 0
  );

  const refreshCode = () => {
    fetchTimestamp().catch(console.trace);
    setLocalTimestamp(Date.now());
    setAnimationTopUpStore({ hasScanned: false });
  };

  useEffect(() => {
    if (!isCertified && !Boolean(isSubmitTask)) {
      const timer = setInterval(refreshCode, 10000);
      return () => clearInterval(timer);
    }
    const timer = setInterval(() => {
      if (Date.now() - localTimestamp > 1000 * 60 * 3) {
        refreshCode();
      }
    }, 1000 * 3);
    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localTimestamp, isCertified, isSubmitTask]);

  return (
    <PaymentWayWrapper style={style}>
      <PaySuccessSocket />
      <QRCodeContainer>
        <QRCode size={130} value={wholeText()} />
        {disabledQrCode() && <QRCodeLoading />}
        {disabledQrCode() && <HiddenTopLeft />}
        {hasScanned && (
          <RefreshButton onClick={refreshCode}>
            <img src={RefreshIcon} alt="refresh" />
            <span>{t("topup_payment_refresh")}</span>
          </RefreshButton>
        )}
      </QRCodeContainer>
      <PaymentContent>
        <div>
          <Price>
            <span>{t("topup_payment_meoney")}</span>
            <span className={"unit"}>{currencySymbol}</span>
            <span className={"price"}>{Number(amount)} </span>
          </Price>
          {((balanceTipList && balanceTipList.length > 0) ||
            (extraTipList && extraTipList.length > 0) ||
            (qingyunTipList && qingyunTipList.length > 0) ||
            (desktopTipList && desktopTipList.length > 0)) && (
            <Space>
              <span>{t("activity_qing_cloud_arrive")}</span>
              {balanceTipList && balanceTipList.length > 0 && (
                <Gift>
                  <span>{t("topup_balance")}</span>&nbsp;&nbsp;
                  <span>
                    {balanceTipList.map((item, index: number) => (
                      <span key={index} className={`balance-${index}`}>
                        <span>{item.pre}</span>
                        <span className="balance-num">{item.content} </span>
                        {index < balanceTipList.length - 1 && (
                          <span className="space">|</span>
                        )}
                      </span>
                    ))}
                  </span>
                </Gift>
              )}
              {extraTipList && extraTipList.length > 0 && (
                <>
                  {extraTipList.map((item, index: number) => (
                    <Gift key={index}>
                      <span>{item.pre}</span>&nbsp;&nbsp;
                      <span className="gift-num">
                        <span>{item.contentPre}</span>
                        <span>
                          {Number(
                            item.content
                              .reduce((acc, v) => acc + Number(v), 0)
                              .toFixed(2)
                          )}
                        </span>
                      </span>
                      <span className="gift-num">{item.contentSuf}</span>
                    </Gift>
                  ))}
                </>
              )}
              {qingyunTipList && qingyunTipList.length > 0 && (
                <>
                  {qingyunTipList.map((item, index: number) => (
                    <Gift key={index}>
                      <span>{item.pre}</span>&nbsp;&nbsp;
                      <span className="gift-num">
                        {item.content.map((contentItem: string, i: number) => (
                          <span key={i}>{contentItem}</span>
                        ))}
                      </span>
                    </Gift>
                  ))}
                </>
              )}
              {desktopTipList && desktopTipList.length > 0 && (
                <>
                  {desktopTipList.map((item, index: number) => (
                    <Gift key={index}>
                      <span>{item.pre}</span>&nbsp;&nbsp;
                      <span className="gift-num">
                        {item.content.map((contentItem: string, i: number) => (
                          <span key={i}>{contentItem}</span>
                        ))}
                      </span>
                    </Gift>
                  ))}
                </>
              )}
            </Space>
          )}
        </div>
        <PayType>
          <img src={Wechat} alt="" className="wechat" />
          <img src={Alipay} alt="" className="ali" />
          <span>{t("topup_payment_text")}</span>
          <button
            onClick={() => {
              onClickCantScanCode?.();
            }}
            disabled={disabledQrCode()}
            className={disabledQrCode() ? "disabled" : ""}
          >
            {paying ? "wait..." : t("recharge_cannot_scan")}
            {!paying && <RightOutlined />}
          </button>
        </PayType>
      </PaymentContent>
    </PaymentWayWrapper>
  );
}
